/**
 * Canonical AI add-on pricing catalog — must match Osync-AWS
 * {@code AddonUsageConstants.AiAddonPricingFeature} and migration seed rows
 * ({@code AddonUsageTableMigration}).
 *
 * Use these wherever the UI references a pricing feature display name, flag, key,
 * or AppContext property key so renames stay consistent across repos.
 */

export type AiAddonFeatureId =
    | "REPHRASE"
    | "AUTO_REPLY"
    | "CALL_SUMMARY"
    | "CALL_INTERACTION"
    | "MESSAGE_REPLY_SUGGESTION"
    | "TRIGGER";

export interface AiAddonFeatureEntry {
    id: AiAddonFeatureId;
    featureName: string;
    featureFlag: number;
    featureKey: string;
    appPropertyKey: string | null;
    description: string;
}

const AI_ADDON_FEATURE: readonly AiAddonFeatureEntry[] = Object.freeze([
    {
        id: "REPHRASE",
        featureName: "Rephrase",
        featureFlag: 100,
        featureKey: "AI_Messaging",
        appPropertyKey: "isRephraseEnabled",
        description: "Rewrite your message with improved tone and clarity.",
    },
    {
        id: "AUTO_REPLY",
        featureName: "Auto Reply",
        featureFlag: 101,
        featureKey: "AI_Messaging",
        appPropertyKey: "isAutoReplyEnabled",
        description: "Automatically respond to inbound messages based on your rules.",
    },
    {
        id: "CALL_SUMMARY",
        featureName: "Call Summary",
        featureFlag: 102,
        featureKey: "AI_Call",
        appPropertyKey: "isCallSummaryEnabled",
        description: "Generate an AI summary after a completed call.",
    },
    {
        id: "CALL_INTERACTION",
        featureName: "Call Interaction",
        featureFlag: 103,
        featureKey: "AI_Call",
        appPropertyKey: null,
        description: "AI-assisted context and actions for live or completed calls.",
    },
    {
        id: "MESSAGE_REPLY_SUGGESTION",
        featureName: "Message Reply Suggestion",
        featureFlag: 104,
        featureKey: "AI_Messaging",
        appPropertyKey: "isAutoSuggestionEnabled",
        description: "Suggested replies while you compose messages.",
    },
    {
        id: "TRIGGER",
        featureName: "Trigger",
        featureFlag: 105,
        featureKey: "Workflow",
        appPropertyKey: null,
        description: "Run configured workflow actions when the trigger conditions are met.",
    },
]);

const getFeatureById = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry])) as Record<
        AiAddonFeatureId,
        AiAddonFeatureEntry
    >
);

const getFeatureByFeatureName = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureName, entry])) as Record<
        string,
        AiAddonFeatureEntry
    >
);

const getFeatureByFeatureFlag = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureFlag, entry])) as Record<
        number,
        AiAddonFeatureEntry
    >
);

/** {@code PricingFeaturesEntity.featureName} values for the AI add-on group. */
export const AI_ADDON_FEATURE_NAMES = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry.featureName])) as Record<
        AiAddonFeatureId,
        string
    >
);

/** {@code AiAddonPricingFeature.featureFlag} values. */
export const AI_ADDON_FEATURE_FLAGS = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.id, entry.featureFlag])) as Record<
        AiAddonFeatureId,
        number
    >
);

export const AI_ADDON_FEATURE_KEYS = Object.freeze({
    AI_MESSAGING: "AI_Messaging",
    AI_CALL: "AI_Call",
    WORKFLOW: "Workflow",
});

/**
 * AppContext (`appProperties`) nested object key for AI feature enabled flags.
 * Use `appProperties.aiFeatures.isRephraseEnabled` etc. — not top-level keys.
 */
export const AI_FEATURES_CONTEXT_KEY = "aiFeatures" as const;

export type AiFeatureAppPropertyKey = NonNullable<AiAddonFeatureEntry["appPropertyKey"]>;

export type AppPropertiesAiFeatures = Partial<Record<AiFeatureAppPropertyKey, boolean>>;

/**
 * AppContext (`appProperties`) keys for AI feature enabled flags, keyed by catalog id.
 * Values are stored under {@link AI_FEATURES_CONTEXT_KEY}, not on `appProperties` directly.
 * Populated from IntegrationResponse on load and updated after Settings → AI Features toggles.
 * MESSAGE_REPLY_SUGGESTION maps to `isAutoSuggestionEnabled` (legacy API field name).
 */
export const AI_APP_PROPERTY_KEYS = Object.freeze(
    Object.fromEntries(
        AI_ADDON_FEATURE.filter((entry) => entry.appPropertyKey).map((entry) => [
            entry.id,
            entry.appPropertyKey,
        ])
    ) as Record<AiAddonFeatureId, string>
);

export const AI_FEATURE_DESCRIPTIONS_BY_FEATURE_NAME = Object.freeze(
    Object.fromEntries(AI_ADDON_FEATURE.map((entry) => [entry.featureName, entry.description])) as Record<
        string,
        string
    >
);

export const getAiAddonFeatureById = (id: string): AiAddonFeatureEntry | undefined =>
    getFeatureById[id as AiAddonFeatureId];

export const getAiAddonFeatureByFlag = (featureFlag: number | string): AiAddonFeatureEntry | undefined => {
    const normalizedFeatureFlag = typeof featureFlag === "number" ? featureFlag : Number(featureFlag);
    if (!Number.isFinite(normalizedFeatureFlag)) {
        return undefined;
    }
    return getFeatureByFeatureFlag[normalizedFeatureFlag];
};

export const getAiAddonFeatureByName = (featureName: string): AiAddonFeatureEntry | undefined => {
    if (typeof featureName !== "string") {
        return undefined;
    }
    return getFeatureByFeatureName[featureName];
};

export const getAIFeatureDescriptionForListing = (
    featureName?: string,
    descriptionFromApi?: string
): string => {
    if (typeof descriptionFromApi === "string" && descriptionFromApi.trim() !== "") {
        return descriptionFromApi.trim();
    }
    if (!featureName || typeof featureName !== "string") {
        return "";
    }
    return AI_FEATURE_DESCRIPTIONS_BY_FEATURE_NAME[featureName] ?? "";
};
